import React from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceResultChip } from '@/components/AbsenceApplication/AbsenceResultChip';
import { AbsenceResultChipKind } from '@/types/components';
import { useAbsenceState } from '@/components/AbsenceApplication/hooks/useAbsenceState';
import type { NonAttendanceApplicationDetail } from '@/components/AbsenceApplication/types';
import { Alert } from '@/components/Icons/Alert';
import { CheckCircleFilled } from '@/components/Icons/CheckCircleFilled';
import { Retry } from '@/components/Icons/Retry';
import { tv } from '@/utils/tv';
import { countDaysInclusive, formatDateTime } from '@/utils/date';

const { Info, Warning, Error: ChipError } = AbsenceResultChipKind;

interface Props {
  application: NonAttendanceApplicationDetail;
  isRetrying: boolean;
  isDimmed?: boolean;
  hideRetryButton?: boolean;
  onRetry: () => void;
}

const card = tv({
  base: 'flex w-full flex-col gap-4 rounded-xl bg-card p-4 shadow-card',
  variants: {
    isDimmed: {
      true: 'opacity-50',
      false: '',
    },
  },
});

export const AbsenceDetailCard: React.FC<Props> = ({
  application,
  isRetrying,
  isDimmed = false,
  hideRetryButton = false,
  onRetry,
}) => {
  const { t } = useTranslation();
  const { absencePeriod, reasonText, createdBy } = application;
  const daysCount = countDaysInclusive(absencePeriod.from, absencePeriod.to);
  const submittedAt = formatDateTime(createdBy.timeModified);
  const {
    cateringLabel,
    isRiga,
    isCancellationError,
    showCateringDivider,
    showCateringPreserved,
    showCateringSuccess,
    showRetry,
    showSentTo,
    periodsMatch,
  } = useAbsenceState(application);

  return (
    <div className={card({ isDimmed })}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-callout-16 text-foreground">
          {t('absenceApplication.result.title')}
        </p>
        <p className="text-caption-12 text-subtle-foreground">
          {t('absenceHistory.days', { count: daysCount })}
        </p>
      </div>

      <div className="flex items-center gap-1.5">
        <CheckCircleFilled className="size-4 shrink-0" />
        <p className="text-body-14 text-muted-foreground">
          {t('absenceApplication.result.absenceSent')}
        </p>
      </div>

      {reasonText && (
        <div className="flex flex-col gap-1 rounded-[10px] bg-background p-3">
          <p className="text-caption-13 text-foreground">
            {t('absenceApplication.detail.reasonTitle')}
          </p>
          <p className="text-body-14 text-subtle-foreground">{reasonText}</p>
        </div>
      )}

      {showCateringDivider && <div className="h-px w-full bg-border" />}

      {showCateringPreserved && (
        <div className="flex flex-col items-start gap-2">
          <div className="flex w-full items-center gap-1.5">
            <CheckCircleFilled className="size-4 shrink-0" />
            <p className="text-body-14 text-muted-foreground">
              {t('absenceApplication.result.cateringNeeded')}
            </p>
          </div>
          <AbsenceResultChip kind={Info} label={cateringLabel} />
        </div>
      )}

      {isCancellationError && (
        <div className="flex flex-col items-start gap-2">
          <div className="flex w-full items-start gap-1.5">
            <Alert className="mt-[2px] size-4 shrink-0 text-destructive" />
            <p className="text-body-14 text-muted-foreground">
              {t('absenceApplication.result.cateringErrored')}
            </p>
          </div>
          <AbsenceResultChip
            kind={ChipError}
            label={t('absenceApplication.result.cateringGeneralError')}
          />
        </div>
      )}

      {showCateringSuccess && (
        <div className="flex flex-col items-start gap-2">
          <div className="flex w-full items-center gap-1.5">
            <CheckCircleFilled className="size-4 shrink-0" />
            <p className="text-body-14 text-muted-foreground">
              {t('absenceApplication.result.cateringNotNeeded')}
            </p>
          </div>
          <AbsenceResultChip
            kind={periodsMatch ? Info : Warning}
            label={cateringLabel}
          />
        </div>
      )}

      <div className="flex flex-col gap-0.5">
        {showSentTo && isRiga && (
          <p className="text-caption-12 self-end text-subtle-foreground">
            {t('absenceApplication.result.sentToRiga')}
          </p>
        )}
        {showSentTo && !isRiga && (
          <p className="text-caption-12 self-end text-subtle-foreground">
            {t('absenceApplication.result.sentToMily')}
          </p>
        )}
        <p className="text-caption-12 self-end text-subtle-foreground">
          {t('absenceApplication.detail.author', {
            name: createdBy.authorName,
            datetime: submittedAt,
          })}
        </p>
      </div>

      {!hideRetryButton && showRetry && !isDimmed && (
        <>
          <div className="h-px w-full bg-border" />
          <button
            type="button"
            onClick={onRetry}
            disabled={isRetrying}
            className="text-subhead-14 flex cursor-pointer items-center justify-center gap-2 font-medium text-primary disabled:cursor-not-allowed"
          >
            <Retry className={`size-4 ${isRetrying ? 'animate-spin' : ''}`} />
            {t('absenceApplication.result.retry')}
          </button>
        </>
      )}
    </div>
  );
};
